import { apiFetcher } from "@/services/API.service";
import { ChatMessage, ChatMessageSchema } from "@/types";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";

// #region getChatHistory
export async function getChatHistory({
  type,
  chatID,
  signal,
  retry = false,
}: {
  type: "task" | "tag" | "search";
  chatID: number[];
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      rData: ChatMessage[];
    }
  | {
      rStatus: "error";
      rMessage:
        | "unauthorized"
        | "network_error"
        | "request_aborted"
        | (string & {});
    }
> {
  if (retry) {
    console.log("Retrying getChatHistory");
  }

  const route =
    type === "task"
      ? `/chat/task/history`
      : type === "tag"
        ? `/chat/tag/history`
        : type === "search"
          ? `/chat/task/history`
          : undefined;
  if (!route) {
    return { rStatus: "error", rMessage: "invalid type" };
  }

  console.log("Getting chat history", type, chatID);

  if (type === "task" && !chatID) {
    return { rStatus: "error", rMessage: "taskID is required" };
  }
  if (type === "tag" && !chatID) {
    return { rStatus: "error", rMessage: "tagID is required" };
  }

  try {
    const response = await apiFetcher({
      route: route,
      method: "POST",
      signal: signal,
      options: {
        body: {
          id: chatID,
        },
      },
    });
    console.log("response", response);
    if (response.status === 200) {
      if (
        !response.data ||
        typeof response.data !== "object" ||
        !("history" in response.data)
      ) {
        return {
          rStatus: "error",
          rMessage: "invalid response: data is not an object with history",
        };
      }

      const validatedData = validateArrayWithSchema<ChatMessage>(
        response.data.history,
        ChatMessageSchema,
        "ChatMessage"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          rMessage:
            "invalid response: data is not an array of valid chat messages",
        };
      }

      return {
        rStatus: "success",
        rData: validatedData,
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        rData: [],
      };
    }

    if (response.status === 400) {
      return { rStatus: "error", rMessage: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", rMessage: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", rMessage: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", rMessage: "request_aborted" };
      }

      if (!retry) {
        return getChatHistory({ type, chatID, signal, retry: true });
      }

      return { rStatus: "error", rMessage: "network_error" };
    }

    return { rStatus: "error", rMessage: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: "failed to fetch" };
  }
}
// #endregion getChatHistory

// #region sendNewMessage
export async function sendNewMessage({
  type,
  chatID,
  message,
}: {
  type: "task" | "tag" | "search";
  chatID: number[];
  message: ChatMessage;
}): Promise<
  | {
      rStatus: "success";
      rData: {
        returnMessage: ChatMessage;
        responseMessage: ChatMessage;
      };
    }
  | {
      rStatus: "error";
      rMessage: "unauthorized" | "network_error" | (string & {});
    }
> {
  try {
    console.log("Sending message", message);

    const route =
      type === "task"
        ? `/chat/task`
        : type === "tag"
          ? `/chat/tag`
          : type === "search"
            ? `/chat/task`
            : undefined;
    if (!route) {
      return { rStatus: "error", rMessage: "invalid type" };
    }

    const response = await apiFetcher({
      route: route,
      method: "POST",
      options: {
        body: {
          id: chatID,
          message: message.message,
        },
      },
    });

    if (response.status === 200) {
      if (
        !response.data ||
        typeof response.data !== "object" ||
        !("returnMessage" in response.data) ||
        !("responseMessage" in response.data)
      ) {
        return {
          rStatus: "error",
          rMessage:
            "invalid response: data is not an object with returnMessage and responseMessage",
        };
      }
      const validReturnMessage = validateWithSchema<ChatMessage>(
        response.data.returnMessage,
        ChatMessageSchema,
        "ChatMessage"
      );
      const validResponseMessage = validateWithSchema<ChatMessage>(
        response.data.responseMessage,
        ChatMessageSchema,
        "ChatMessage"
      );

      if (!validReturnMessage || !validResponseMessage) {
        return {
          rStatus: "error",
          rMessage:
            "invalid response: returnMessage or responseMessage is not a valid ChatMessage",
        };
      }

      return {
        rStatus: "success",
        rData: {
          returnMessage: validReturnMessage,
          responseMessage: validResponseMessage,
        },
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", rMessage: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", rMessage: "route not found" };
    }

    if (response.status === 0) {
      return { rStatus: "error", rMessage: "network_error" };
    }

    return { rStatus: "error", rMessage: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: "failed to fetch" };
  }
}
// #endregion sendNewMessage
